const SCHEMA_PRIVILEGES = [
  'CREATE FILE FORMAT',
  'CREATE STAGE',
  'CREATE TABLE',
  'CREATE TEMPORARY TABLE',
  'USAGE',
].join(', ');

// Backend error codes from SnowflakeSetupError
const BACKEND_SETUP_QUERIES = {
  MISSING_WAREHOUSE_USAGE: {
    step: 'Step 3: Role Setup',
    verify: (c) => `SHOW GRANTS ON WAREHOUSE ${c.warehouse};`,
    fix: (c) =>
      `USE ROLE ACCOUNTADMIN;\nGRANT USAGE ON WAREHOUSE ${c.warehouse} TO ROLE ${c.role};`,
  },
  DATABASE_SCHEMA_NOT_FOUND: {
    step: 'Step 3: Role Setup',
    verify: (c) =>
      `SHOW SCHEMAS LIKE '${c.snowflake_schema}' IN DATABASE ${c.database};`,
    fix: (c) =>
      `USE ROLE SYSADMIN;\nCREATE DATABASE IF NOT EXISTS ${c.database};\nCREATE SCHEMA IF NOT EXISTS ${c.database}.${c.snowflake_schema};\n\nUSE ROLE ACCOUNTADMIN;\nGRANT USAGE ON DATABASE ${c.database} TO ROLE ${c.role};\nGRANT ${SCHEMA_PRIVILEGES} ON SCHEMA ${c.database}.${c.snowflake_schema} TO ROLE ${c.role};`,
  },
  MISSING_SCHEMA_PRIVILEGES: {
    step: 'Step 3: Role Setup',
    verify: (c) => `SHOW GRANTS ON SCHEMA ${c.database}.${c.snowflake_schema};`,
    fix: (c) =>
      `USE ROLE ACCOUNTADMIN;\nGRANT ${SCHEMA_PRIVILEGES} ON SCHEMA ${c.database}.${c.snowflake_schema} TO ROLE ${c.role};`,
  },
  STORAGE_INTEGRATION_NOT_FOUND: {
    step: 'Step 4: Storage Integration Setup',
    verify: (c) => `SHOW INTEGRATIONS LIKE '${c.storage_integration_name}';`,
    fix: (c) =>
      `USE ROLE ACCOUNTADMIN;\nCREATE STORAGE INTEGRATION ${c.storage_integration_name}\n  TYPE = EXTERNAL_STAGE\n  STORAGE_PROVIDER = S3\n  ENABLED = TRUE\n  STORAGE_AWS_ROLE_ARN = 'arn:aws:iam::0000:role/dummy-role'\n  STORAGE_ALLOWED_LOCATIONS = ('s3://dummy-bucket');\n\nGRANT USAGE, MODIFY ON INTEGRATION ${c.storage_integration_name} TO ROLE ${c.role};`,
  },
  // Role can use the integration but cannot configure it. USAGE allows DESC + creating stages;
  // MODIFY lets Integrate.io set the role ARN / allowed locations. OWNERSHIP is NOT required.
  MISSING_INTEGRATION_ACCESS: {
    step: 'Step 4: Storage Integration Setup',
    verify: (c) => `SHOW GRANTS ON INTEGRATION ${c.storage_integration_name};`,
    fix: (c) =>
      `USE ROLE ACCOUNTADMIN;\nGRANT USAGE, MODIFY ON INTEGRATION ${c.storage_integration_name} TO ROLE ${c.role};`,
  },
  // Raised when the integration still needs configuring but the role lacks MODIFY.
  MISSING_INTEGRATION_MODIFY: {
    step: 'Step 4: Storage Integration Setup',
    verify: (c) => `SHOW GRANTS ON INTEGRATION ${c.storage_integration_name};`,
    fix: (c) =>
      `USE ROLE ACCOUNTADMIN;\nGRANT USAGE, MODIFY ON INTEGRATION ${c.storage_integration_name} TO ROLE ${c.role};`,
  },
};

// Ghibli validation server action names
const VALIDATION_SERVER_GRANT_TARGETS = {
  warehousePrivilege: {
    step: 'Step 3: Role Setup',
    grantTarget: (c) => `WAREHOUSE ${c.warehouse}`,
    verify: (c) => `SHOW GRANTS ON WAREHOUSE ${c.warehouse};`,
  },
  databasePrivilege: {
    step: 'Step 3: Role Setup',
    grantTarget: (c) => `DATABASE ${c.database}`,
    verify: (c) => `SHOW GRANTS ON DATABASE ${c.database};`,
  },
  schemaPrivilege: {
    step: 'Step 3: Role Setup',
    grantTarget: (c) => `SCHEMA ${c.database}.${c.snowflake_schema}`,
    verify: (c) => `SHOW GRANTS ON SCHEMA ${c.database}.${c.snowflake_schema};`,
  },
  futureGrantsOnSchema: {
    step: 'Step 3: Role Setup',
    grantTarget: () => null,
    verify: (c) =>
      `SHOW FUTURE GRANTS IN SCHEMA ${c.database}.${c.snowflake_schema};`,
    // This check fails only when a FUTURE TABLES OWNERSHIP grant already exists pointing at a
    // DIFFERENT role. The role owns the tables it creates, so no future-ownership grant is needed
    // by default — the fix is to reassign the conflicting grant to this role (or drop it), not to
    // add a new broad grant. Verify the SHOW FUTURE GRANTS output to find the conflicting grantee.
    fix: (c) =>
      `-- A future-tables OWNERSHIP grant points at another role. Reassign it to this role:\nUSE ROLE ACCOUNTADMIN;\nGRANT OWNERSHIP ON FUTURE TABLES IN SCHEMA ${c.database}.${c.snowflake_schema} TO ROLE ${c.role} COPY CURRENT GRANTS;`,
  },
};

function buildGrantFix(missingPrivileges, grantTarget, role) {
  const grants = missingPrivileges
    .map((priv) => `GRANT ${priv} ON ${grantTarget} TO ROLE ${role};`)
    .join('\n');
  return `USE ROLE ACCOUNTADMIN;\n${grants}`;
}

// Connection error message patterns that map to setup steps
const CONNECTION_ERROR_PATTERNS = [
  {
    pattern: /role .* does not exist or not authorized/i,
    step: 'Step 3: Role Setup',
    verify: (c) => `SHOW ROLES LIKE '${c.role}';`,
    fix: (c) =>
      `USE ROLE SECURITYADMIN;\nCREATE ROLE IF NOT EXISTS ${c.role};\nGRANT ROLE ${c.role} TO ROLE SYSADMIN;`,
  },
  {
    pattern: /role .* is not granted to this user/i,
    step: 'Step 3: Role Setup',
    verify: (c) => `SHOW GRANTS TO USER ${c.username};`,
    fix: (c) =>
      `USE ROLE SECURITYADMIN;\nGRANT ROLE ${c.role} TO USER ${c.username};`,
  },
];

export function getSnowflakeConnectionErrorInfo(message, connectionConfig) {
  if (!message) {
    return null;
  }
  for (const entry of CONNECTION_ERROR_PATTERNS) {
    if (entry.pattern.test(message)) {
      return {
        step: entry.step,
        verifyQuery: connectionConfig ? entry.verify(connectionConfig) : '',
        fixQuery: connectionConfig ? entry.fix(connectionConfig) : '',
      };
    }
  }
  return null;
}

export function getSnowflakeSetupErrorInfo(code, connectionConfig) {
  const entry = BACKEND_SETUP_QUERIES[code];
  if (!entry) {
    return null;
  }
  return {
    step: entry.step,
    verifyQuery: connectionConfig ? entry.verify(connectionConfig) : '',
    fixQuery: connectionConfig ? entry.fix(connectionConfig) : '',
  };
}

export function getSnowflakeValidationServerErrorInfo(
  action,
  connectionConfig,
  data,
) {
  const entry = VALIDATION_SERVER_GRANT_TARGETS[action];
  if (!entry) {
    return null;
  }
  const verifyQuery = connectionConfig ? entry.verify(connectionConfig) : '';
  let fixQuery = '';
  if (connectionConfig) {
    if (entry.fix) {
      fixQuery = entry.fix(connectionConfig);
    } else if (
      data &&
      data.missingPrivileges &&
      data.missingPrivileges.length
    ) {
      const target = entry.grantTarget(connectionConfig);
      if (target) {
        fixQuery = buildGrantFix(
          data.missingPrivileges,
          target,
          connectionConfig.role,
        );
      }
    }
  }
  return { step: entry.step, verifyQuery, fixQuery };
}
